import type { PropsWithChildren } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { wrapperInnerStyle } from 'hb-builder/components/RenderComponents/shared/common/Section/style';
import {
  getVideoPositionOnFillVideo,
  getVideoPositionOnFitVideo,
} from 'hb-builder/components/RenderComponents/shared/common/Section/utils/getVideoPosition';
import { useSectionContext } from 'hb-builder/hooks';
import type { ItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import { Row } from 'hb-react/shared/components/DesignSystem/Grid'; // Ratio used usually for videos

// Ratio used usually for videos
const VIDEO_RATIO = 16 / 9;

export interface RowStyleAttributes {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
  borderRadius?: string;
  flexDirection?: string;
  height?: string;
  justifyContent?: string;
  maxWidth?: string;
  padding?: string;
  width?: string;
}

const VideoSection = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    attributes: ItemAttributes;
    isMobile: boolean;
    rowStyle?: RowStyleAttributes;
  }>
>(({ attributes, children, isMobile, rowStyle }, ref) => {
  const [videoPlay, setVideoPlay] = useState(true);
  const [videoUrl, setVideoUrl] = useState('');

  const { sectionHeight } = useSectionContext();

  const isFillVideo = attributes?.videoBackgroundArrangement === 'fill';
  const darkWidthBg = attributes && attributes.height * VIDEO_RATIO;

  const handleKeyDown = useCallback(() => {
    setVideoPlay(!videoPlay);
  }, [videoPlay]);

  useEffect(() => {
    // Accessibility: we are playing or pausing the video by pressing on space key
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (attributes?.containerBackgroundVideo) {
      setVideoUrl(attributes?.containerBackgroundVideo);
      setVideoPlay(true);
    }
  }, [attributes?.containerBackgroundVideo]);

  const style = { ...(rowStyle || {}), width: '100%' };

  return (
    <StyledRow
      data-testid="video-section"
      $isMobile={isMobile || false}
      $isVideoType
      $minHeight={attributes?.height || 330}
      $mobileDisplay={attributes?.mobileDisplay || 'null'}
      ref={ref}
      style={style}
      $themeOverridden={attributes?.themeOverridden || false}
      $verticalAlign={attributes?.verticalAlign || 'center'}
    >
      <VideoWrapper
        $videoBackgroundPosition={attributes?.videoBackgroundPosition}
        $height={attributes?.height}
      >
        <PlayerWrapper
          $videoBackgroundPosition={
            attributes?.videoBackgroundPosition || 'center'
          }
          $sectionHeight={sectionHeight}
          $height={attributes?.height}
          $isFillVideo={isFillVideo}
          $isMobile={isMobile}
        >
          <ReactPlayer
            className="player"
            data-testid="video-react-player"
            url={videoUrl}
            playing={videoPlay}
            loop
            controls={false}
            height="-webkit-fill-available"
            width="100%"
          />
        </PlayerWrapper>
        <StyledBox
          $isFillVideo={isFillVideo}
          $darkenedVideo={attributes?.darkenedVideo}
        >
          {children}
        </StyledBox>
        <DarkBackground
          $darkenedVideo={attributes?.darkenedVideo}
          $isFillVideo={isFillVideo}
          $darkWidthBg={darkWidthBg}
        />
      </VideoWrapper>
    </StyledRow>
  );
});

export default VideoSection;

interface StyledRowProps {
  $isMobile: boolean;
  $isVideoType: boolean;
  $minHeight: number;
  $mobileDisplay: string;
  $verticalAlign: SectionItemAttributes['verticalAlign'];
  $themeOverridden?: boolean;
  style?: RowStyleAttributes;
}

interface StyledBoxProps {
  $darkenedVideo?: boolean;
  $isFillVideo?: boolean;
}

interface VideoWrapperProps {
  $height?: number;
  $videoBackgroundPosition?: string;
}

interface PlayerWrapperProps {
  $videoBackgroundPosition: string;
  $sectionHeight?: number;
  $height?: number;
  $isFillVideo?: boolean;
  $isMobile?: boolean;
}

const StyledRow = styled(Row)<StyledRowProps>`
  ${wrapperInnerStyle}
  background-image: ${({ $isVideoType }: { $isVideoType: boolean }) =>
    $isVideoType && `none`} !important;
  ${({ $themeOverridden }: { $themeOverridden?: boolean }) =>
    !$themeOverridden &&
    css`
      * {
        font: inherit;
        font-size: 100%;
      }
    `}
`;

const StyledBox = styled(Box).attrs<{ $isFillVideo: boolean }>(
  ({ $isFillVideo }) => ({
    px: $isFillVideo
      ? {
          _: 4,
          m: 6,
          l: 8,
        }
      : undefined,
  }),
)<StyledBoxProps>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $darkenedVideo, $isFillVideo }) =>
    $darkenedVideo && $isFillVideo && 'rgba(0, 0, 0, 0.4)'} !important;
  z-index: ${({ $darkenedVideo, $isFillVideo }) =>
    $darkenedVideo && !$isFillVideo && '1'};
`;

const PlayerWrapper = styled(Box)<PlayerWrapperProps>`
  width: ${({ $isFillVideo }) => $isFillVideo && '100%'};
  height: ${({ $isFillVideo, $height }) => !$isFillVideo && `${$height}px`};
  position: ${({ $isFillVideo }) => ($isFillVideo ? 'absolute' : 'relative')};
  right: 0;
  ${getVideoPositionOnFillVideo}
`;

const VideoWrapper = styled(Box).attrs<VideoWrapperProps>(({ $height }) => ({
  height: $height,
  width: '100%',
}))<VideoWrapperProps>`
  overflow: hidden;
  position: relative;
  ${getVideoPositionOnFitVideo}
`;

const DarkBackground = styled(Box)<{
  $darkenedVideo?: boolean;
  $isFillVideo?: boolean;
  $darkWidthBg?: number;
}>`
  height: 100%;
  position: absolute;
  display: ${({ $isFillVideo }) => $isFillVideo && 'none'};
  width: ${({ $darkWidthBg }) => `${$darkWidthBg}px`};
  background-color: ${({ $darkenedVideo }) =>
    $darkenedVideo && 'rgba(0, 0, 0, 0.4)'} !important;
`;
